import React, { createContext, ReactElement, ReactNode, useContext, useReducer } from 'react';

export enum ACTION_TYPE {
    SET_HAS_CHANGE = 'SET_HAS_CHANGE',
    SET_PAGE = 'SET_PAGE',
}

export const initialState: FormChangesStateType = {
    hasChange: false,
    page: 0,
};

type SetHasChange = {
    type: ACTION_TYPE.SET_HAS_CHANGE;
    payload: boolean;
};

type SetPagination = {
    type: ACTION_TYPE.SET_PAGE;
    payload: number;
};

export type FormChangesStateType = {
    hasChange: boolean;
    page: number;
};

export type FormChangesDispatchType = {
    setHasChange: (hasChange: boolean) => void;
    setPage: (page: number) => void;
};

type FormChangesActionType = SetHasChange | SetPagination;

export const useFormReducer = (state: FormChangesStateType, action: FormChangesActionType): FormChangesStateType => {
    const { type, payload } = action;
    switch (type) {
        case ACTION_TYPE.SET_PAGE: {
            return {
                ...state,
                page: payload,
            };
        }
        case ACTION_TYPE.SET_HAS_CHANGE:
        default: {
            return {
                ...state,
                hasChange: payload,
            };
        }
    }
};

type FormChangesProviderType = {
    children: ReactNode;
};

export const FormDispatchContext = createContext({} as FormChangesDispatchType);
export const FormStateContext = createContext({} as FormChangesStateType);

const dispatchFormChangesView = (): FormChangesDispatchType => useContext(FormDispatchContext);
const useFormChangesView = (): FormChangesStateType => useContext(FormStateContext);

const FormChangesViewProvider = (props: FormChangesProviderType): ReactElement => {
    const { children } = props;
    const [state, dispatch] = useReducer(useFormReducer, initialState);

    const setHasChange = (hasChange: boolean) =>
        dispatch({
            type: ACTION_TYPE.SET_HAS_CHANGE,
            payload: hasChange,
        });

    const setPagination = (page: number) =>
        dispatch({
            type: ACTION_TYPE.SET_PAGE,
            payload: page,
        });

    return (
        <FormStateContext.Provider value={state}>
            <FormDispatchContext.Provider
                value={{
                    setHasChange,
                    setPage: setPagination,
                }}
            >
                {children}
            </FormDispatchContext.Provider>
        </FormStateContext.Provider>
    );
};

export { FormChangesViewProvider, dispatchFormChangesView, useFormChangesView };
